/**
 * This file is part of the Goobi viewer - a content presentation and management
 * application for digitized objects.
 * 
 * Visit these websites for more information.
 *          - https://goobi.io
 *          - https://www.intranda.com
 *          - https://github.com/intranda/goobi
 * 
 * This program is free software; you can redistribute it and/or modify it under the terms
 * of the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 * 
 * This program is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR A
 * PARTICULAR PURPOSE. See the GNU General Public License for more details.
 * 
 * You should have received a copy of the GNU General Public License along with this
 * program. If not, see <http://www.gnu.org/licenses/>.
 * 
 * Module which handles the image controls of the image view.
 * 
 * @version 3.2.0
 * @module viewImage.tools.areaSelect
 * @requires jQuery
 */

import { Subject, fromEventPattern } from "rxjs";


const _debug = false;

const _hbAdd = 5;
const _minDistanceToExistingRect = 0.01;

const _config = {
    rotateWithImage: true,
    style : {
        borderColor: "green",
        borderWidth: 2,
        fillOpacity: 0.5,
        className: "imageview-overlay"
    }
}

class Draw {

    constructor(viewer, config, startCondition) {
        this.viewer = viewer;
        this.config = jQuery.extend(true, {}, _config, config);
        this.startCondition = startCondition;
        this.drawing = false;
        this.currentRect = null;
        this.startPoint = null;
        this.finishedObservable = new Subject();

        this.subscriptions = [
            fromEventPattern( handler => this.viewer.openseadragon.addHandler("canvas-press", handler), handler => this.viewer.openseadragon.removeHandler("canvas-press", handler))
            .subscribe(e => this.handlePress(e)),
            fromEventPattern( handler => this.viewer.openseadragon.addHandler("canvas-drag", handler), handler => this.viewer.openseadragon.removeHandler("canvas-drag", handler))
            .subscribe(e => this.handleDrag(e)),
            // fromEventPattern( handler => this.viewer.openseadragon.addHandler("canvas-drag-end", handler), handler => this.viewer.openseadragon.removeHandler("canvas-drag-end", handler))
            // .subscribe(e => this.handleDragEnd(e)),
            fromEventPattern( handler => this.viewer.openseadragon.addHandler("canvas-release", handler), handler => this.viewer.openseadragon.removeHandler("canvas-release", handler))
            .subscribe(e => this.handleRelease(e))
        ]

        // this.viewer.openseadragon.addHandler("canvas-press", e => this.handlePress(e));
        // this.viewer.openseadragon.addHandler("canvas-drag", e => this.handleDrag(e));
        // this.viewer.openseadragon.addHandler("canvas-drag-end", e => this.handleDragEnd(e));
        // this.viewer.openseadragon.addHandler("canvas-release", e => this.handleRelease(e));

    }

    finishedDrawing() {
        return this.finishedObservable;
    }

    close() {
        this.finishedObservable.complete();
        this.finishedObservable.unsubscribe();
        this.subscriptions.forEach(subscr => subscr.unsubscribe());
        this.subscriptions = [];
    }

    isDrawing() {
        return this.drawing;
    }

    handleDrag(event) {
        if ( this.drawing && this.startCondition(event.originalEvent)) {
            var position = new OpenSeadragon.Point(event.position.x, event.position.y);//.times(1/window.devicePixelRatio);
            this.updateRect(position);
        }
    }

    handlePress(event) {
        if ( this.startCondition(event.originalEvent)) {
            this.drawing = true;
            var position = new OpenSeadragon.Point(event.position.x, event.position.y);//.times(1/window.devicePixelRatio);
            this.createEmptyRectAt(position);
            this.viewer.disablePan();
        }
    }

    handleRelease(event) {
        if(this.drawing && this.startCondition(event.originalEvent)) {
            this.drawing = false;
            this.finishRect();
            this.updateRect();
            this.viewer.enablePan();
        }
        this.currentRect = undefined;
    }

    finishRect() {
        if(this.currentRect && this.currentRect.width * this.currentRect.height > 0) {
            const rotation = this.config.rotateWithImage ? this.viewer.openseadragon.viewport.getRotation() : 0;
            const rect = ImageView.CoordinateConversion.convertCoordinatesFromCanvasToImage(this.currentRect, this.viewer.openseadragon);
            if(_debug)console.log("finished drawing ", this.currentRect, rect);
            this.finishedObservable.next({
                bounds: {
                    canvas: this.currentRect.clone().rotate(rotation),
                    viewport: rect.rotate(rotation)
                },
                style : this.config.style
            });
        }
    }

    createEmptyRectAt(position) {
        this.currentRect = new OpenSeadragon.Rect(position.x, position.y, 0,0);
        this.startPoint = position;
        _drawRect(this.currentRect, this.config.style, this.viewer.openseadragon.drawer.context);
        
    }

    updateRect(position) {
        this.viewer.openseadragon.forceRedraw();
        if(this.currentRect) {
            this.viewer.openseadragon.addOnceHandler( 'update-viewport', ( event ) => {
                if(this.isDrawing()) {
                    this.currentRect.x = Math.min(this.startPoint.x, position.x);
                    this.currentRect.y = Math.min(this.startPoint.y, position.y);
                    this.currentRect.width = Math.abs(this.startPoint.x-position.x);
                    this.currentRect.height = Math.abs(this.startPoint.y-position.y);
                    _drawRect(this.currentRect, this.config.style, this.viewer.openseadragon.drawer.context);
                }
            });
        }
    }


}

function _drawRect(rect, style, context) {
    rect = rect.times(window.devicePixelRatio);
    context.beginPath();        
    context.lineWidth = style.borderWidth;
    context.strokeStyle = style.borderColor;
    context.fillStyle = style.fillColor;
    context.rect(rect.x, rect.y, rect.width, rect.height);
    context.stroke();
    if(style.fillColor) {
        context.fill();
    }
}

export default Draw;

